import { voiceDestination, voicePurpose, voiceOutcome, voiceAnswer, voiceDisconnect, voiceProviderStatus } from "./voiceCallLabels";
import withFinancialSession from "./withFinancialSession";
import VoiceAgentTest from "./VoiceAgentTest";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppAPIService } from "../../../../services/AppAPIService";
import InputDateForm from "../../../../components/InputDateForm";
import {
  isFinancialDashboardEnabled,
  isFinancialAdminPreviewUser,
} from "../../../../utils/FeatureFlags";
import {
  financialLocalDate,
  financialDateForPicker,
  financialDateFromPicker,
} from "./financialPresentation";
import {
  validVoiceReport,
  voiceMoney,
  voiceReportCsv,
} from "./voiceReportContract";
import styles from "./collections.module.scss";
import voice from "./voice.module.scss";

const initialPeriod = () => {
  const today = financialLocalDate();
  return { dateFrom: today.slice(0, 7) + "-01", dateTo: today };
};
const defaultLoad = (params) =>
  AppAPIService.getInstance().makeHttpRequest({
    method: "GET",
    sensitive: true,
    url: `/financial/voice-archive/report?${new URLSearchParams({
      ...params,
      pageSize: "25",
    })}`,
  });
const seconds = (value) =>
  value === null
    ? "Pendente"
    : `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(
        value
      )} s`;
const dateTime = (value) =>
  new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(value));
const mediaLabel = (value) =>
  ({
    complete: "Áudio e transcrição arquivados",
    pending: "Cópia pendente",
    "not-retained-by-provider": "Sem gravação na origem",
    "not-connected": "Ligação não conectada",
  }[value] || "Em conferência");

function FinancialVoicePage({ load = defaultLoad }) {
  const user = useSelector((state) => state.auth.user);
  const allowed =
    isFinancialDashboardEnabled() && isFinancialAdminPreviewUser(user);
  const access = useRef(null);
  access.current = allowed ? `${user?.id || user?.email}:${user?.role}` : null;
  useEffect(
    () => () => {
      access.current = null;
    },
    []
  );
  const [period, setPeriod] = useState(initialPeriod);
  const [applied, setApplied] = useState(initialPeriod);
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [media, setMedia] = useState({ busy: "", error: "" });
  useEffect(() => {
    if (window.setPageTitle) window.setPageTitle("Ligações e custos");
  }, []);
  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    setState({ loading: true, data: null, error: "" });
    Promise.resolve()
      .then(() => load({ ...applied, page: String(page) }))
      .then((data) => {
        if (!validVoiceReport(data, applied, page))
          throw new Error("INVALID_VOICE_REPORT");
        if (active) setState({ loading: false, data, error: "" });
      })
      .catch((error) => {
        if (active)
          setState({
            loading: false,
            data: null,
            error: [401, 403].includes(error?.response?.status)
              ? "Seu usuário não tem acesso a este relatório."
              : "Não foi possível conferir as ligações. Tente atualizar novamente.",
          });
      });
    return () => {
      active = false;
    };
  }, [allowed, applied, page, revision, load]);
  useEffect(() => {
    if (!state.data) return undefined;
    const timer = setTimeout(
      () =>
        setState({
          loading: false,
          data: null,
          error: "Esta consulta expirou. Atualize para conferir os valores.",
        }),
      Math.max(0, Date.parse(state.data.expiresAt) - Date.now())
    );
    return () => clearTimeout(timer);
  }, [state.data]);
  if (!allowed)
    return <p role="status">Relatório indisponível para este acesso.</p>;
  const { data, loading, error } = state;
  const summary = data?.summary;
  const exportPage = () => {
    const url = URL.createObjectURL(
      new Blob([voiceReportCsv(data)], { type: "text/csv;charset=utf-8" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `ligacoes-${applied.dateFrom}-${applied.dateTo}-pagina-${page}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const openMedia = async (row, kind) => {
    const identity = access.current;
    setMedia({ busy: `${row.callId}-${kind}`, error: "" });
    try {
      const result = await AppAPIService.getInstance().makeHttpRequest({
        method: "GET",
        sensitive: true,
        url: `/financial/voice-archive/calls/${row.callId}/media/${kind}`,
      });
      if (!identity || access.current !== identity) return;
      const url = new URL(result.url);
      if (
        url.protocol !== "https:" ||
        url.hostname !==
          "xavier-voice-archive-410210175717-us-east-2.s3.us-east-2.amazonaws.com" ||
        url.username ||
        url.password ||
        result.expiresInSeconds !== 60
      )
        throw new Error("INVALID_MEDIA_LINK");
      const link = document.createElement("a");
      link.href = result.url;
      link.rel = "noopener noreferrer";
      link.referrerPolicy = "no-referrer";
      link.click();
      setMedia({ busy: "", error: "" });
    } catch {
      setMedia({
        busy: "",
        error: "Não foi possível abrir a mídia. Atualize e tente novamente.",
      });
    }
  };
  const run = data?.collection?.lastRun;
  const collectionFresh =
    run &&
    Date.now() - Date.parse(run.checkedAt) < 30 * 60000 &&
    run.complete === true;
  return (
    <main className={styles.page} aria-label="Ligações e custos">
      <div className={styles.toolbar}>
        <Link to="/admin/financeiro/cobrancas">← Voltar às cobranças</Link>
        <span className={styles.safeBadge}>Acesso administrativo</span>
      </div>
      <section className={styles.list}>
        <div className={styles.listHeader}>
          <div>
            <span>Financeiro</span>
            <h2>Custos por provedor</h2>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading}
            onClick={() => setRevision((v) => v + 1)}
          >
            {loading ? "Atualizando…" : "Atualizar"}
          </button>
        </div>
        <div className={voice.intro}>
          <p>
            Acompanhe as ligações arquivadas do agente financeiro. Valores em
            dólares e estimativa histórica em reais.
          </p>
          <details className={voice.scope}>
            <summary>Sobre os custos e a fatura dos provedores</summary>
            <p>
              Este relatório não representa a fatura completa da Retell ou da
              Twilio. Testes de texto, mensalidades dos números, outros
              recursos, impostos e ajustes ficam fora destes totais. A conversão
              usa a PTAX de venda do Banco Central na data da ligação
              (Brasília). Sem fechamento publicado, utiliza o último disponível
              nos 14 dias anteriores, com a data informada. Valores do dia
              corrente são provisórios. O câmbio efetivo do cartão, spread e
              impostos podem diferir. Os totais em reais somam cada ligação pela
              sua própria cotação.
            </p>
          </details>
        </div>
      </section>
      <VoiceAgentTest
        key={`${user?.id || user?.email}:${user?.role}`}
        userKey={user?.id || user?.email}
        onComplete={() => setRevision((v) => v + 1)}
      />
      <div className={voice.filters}>
        <form
          className="xv-inline-filter-row xv-financial-period-filters"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setApplied({ ...period });
            setRevision((v) => v + 1);
          }}
        >
          <InputDateForm
            name="voice-date-from"
            label="Ligações de"
            selectedDate={financialDateForPicker(period.dateFrom)}
            isClearable={false}
            onChange={(d) =>
              setPeriod((p) => ({ ...p, dateFrom: financialDateFromPicker(d) }))
            }
          />
          <InputDateForm
            name="voice-date-to"
            label="Ligações até"
            selectedDate={financialDateForPicker(period.dateTo)}
            isClearable={false}
            onChange={(d) =>
              setPeriod((p) => ({ ...p, dateTo: financialDateFromPicker(d) }))
            }
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              loading ||
              !period.dateFrom ||
              !period.dateTo ||
              period.dateFrom > period.dateTo
            }
          >
            Aplicar filtros
          </button>
        </form>
      </div>
      {loading && (
        <div className={styles.feedback} role="status">
          Conferindo ligações e custos…
        </div>
      )}
      {error && (
        <div className={`${styles.feedback} ${styles.error}`} role="alert">
          {error}
        </div>
      )}
      {media.error && (
        <div className={styles.sourceNote} role="alert">
          {media.error}
        </div>
      )}
      {data && (
        <>
          <section
            className={`${styles.summary} ${voice.summary}`}
            aria-label="Resumo das ligações"
          >
            <div>
              <span>Ligações registradas</span>
              <strong>{summary.calls}</strong>
              <small>
                {seconds(summary.durationMs / 1000)} de duração Retell
                {summary.unknownDurations > 0
                  ? ` · ${summary.unknownDurations} duração(ões) pendente(s)`
                  : ""}
              </small>
            </div>
            <div>
              <span>Retell · ligações</span>
              <strong>{voiceMoney(summary.retellTotalUsd)}</strong>
              <small>
                {voiceMoney(summary.retellTotalBrl, "BRL")} · estimado
              </small>
              <small>
                {summary.retellPending
                  ? `${summary.retellPending} custo(s) pendente(s) · ${
                      summary.calls > summary.retellPending
                        ? `Confirmado: ${voiceMoney(summary.retellKnownUsd)}`
                        : "Nenhum custo confirmado"
                    }`
                  : "Custo de IA registrado"}
              </small>
            </div>
            <div>
              <span>Twilio · conectividade</span>
              <strong>{voiceMoney(summary.twilioTotalUsd)}</strong>
              <small>
                {voiceMoney(summary.twilioTotalBrl, "BRL")} · estimado
              </small>
              <small>
                {summary.twilioPending
                  ? `${summary.twilioPending} custo(s) pendente(s) · ${
                      summary.calls > summary.twilioPending
                        ? `Confirmado: ${voiceMoney(summary.twilioKnownUsd)}`
                        : "Nenhum custo confirmado"
                    }`
                  : "Custo de telefonia conciliado"}
              </small>
            </div>
            <div>
              <span>Total das ligações</span>
              <strong>{voiceMoney(summary.totalUsd)}</strong>
              <small>{voiceMoney(summary.totalBrl, "BRL")} · estimado</small>
              <small>
                {summary.calls * 2 >
                summary.retellPending + summary.twilioPending
                  ? `Subtotal confirmado: ${voiceMoney(
                      summary.knownSubtotalUsd
                    )}`
                  : "Nenhum custo confirmado"}
              </small>
            </div>
          </section>
          <div className={styles.sourceNote} role="status">
            <p>
              Conversão histórica · Banco Central / PTAX de venda.
              {summary.fxPending > 0
                ? ` ${summary.fxPending} ligação(ões) sem cotação confirmada; valores em reais pendentes.`
                : ""}
              {summary.fxProvisional > 0
                ? ` ${summary.fxProvisional} ligação(ões) com cotação provisória do dia corrente.`
                : ""}
            </p>
            {summary.internalTestCalls > 0 && (
              <p>
                {summary.internalTestCalls} teste(s) interno(s) incluído(s) nos
                custos. Esses registros não representam cobranças a clientes.
              </p>
            )}
            <strong>
              {collectionFresh
                ? "Captura em dia"
                : "Captura precisa de conferência"}
            </strong>
            {run?.checkedAt
              ? ` · Última execução: ${dateTime(run.checkedAt)}`
              : " · Sem confirmação da última execução"}
            .
            {!data.collection.twilioEnabled && (
              <p>
                A consulta automática da Twilio aguarda a configuração de acesso
                de leitura. Custos ausentes permanecem pendentes.
              </p>
            )}
            {summary.missingDateRecords > 0 && (
              <p>
                {summary.missingDateRecords} registro(s) sem data de início não
                entraram nos totais deste período.
              </p>
            )}
          </div>
          <section className={styles.list} aria-label="Histórico das ligações">
            <div className={styles.listHeader}>
              <div>
                <span>Horário de Brasília</span>
                <h2>Histórico das ligações</h2>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!data.items.length}
                onClick={exportPage}
              >
                Exportar página CSV
              </button>
            </div>
            {!data.items.length ? (
              <div className={styles.empty}>
                <strong>Nenhuma ligação neste período</strong>
                <p>
                  Altere as datas para consultar outros registros arquivados.
                </p>
              </div>
            ) : (
              data.items.map((row) => (
                <article className={voice.call} key={row.callId}>
                  <div>
                    <span>Ligação</span>
                    <strong>{dateTime(row.startedAt)}</strong>
                    <span>Telefone de destino</span>
                    <strong>{voiceDestination(row)}</strong>
                    <small>Finalidade: {voicePurpose(row)}</small>
                    <code>{row.callId}</code>
                  </div>
                  <div>
                    <span>Duração Retell</span>
                    <strong>
                      {seconds(
                        row.durationMs === null ? null : row.durationMs / 1000
                      )}
                    </strong>
                    <small>Twilio: {seconds(row.twilioDurationSeconds)}</small>
                  </div>
                  <div>
                    <span>Retell</span>
                    <strong>{voiceMoney(row.retellCostUsd)}</strong>
                    <small>
                      {voiceMoney(row.retellCostBrl, "BRL")} · estimado
                    </small>
                    <small>
                      Twilio: {voiceMoney(row.twilioCostUsd)}
                      <br />
                      {voiceMoney(row.twilioCostBrl, "BRL")} · estimado
                    </small>
                  </div>
                  <div>
                    <span>Total da ligação</span>
                    <strong>{voiceMoney(row.totalCostUsd)}</strong>
                    <small>
                      {voiceMoney(row.totalCostBrl, "BRL")} · estimado
                    </small>
                    <small>
                      {row.exchange
                        ? `USD 1 = ${voiceMoney(
                            row.exchange.rate,
                            "BRL"
                          )} · PTAX venda ${row.exchange.rateDate
                            .split("-")
                            .reverse()
                            .join("/")}${
                            row.exchange.provisional ? " · provisória" : ""
                          }`
                        : "Cotação histórica pendente"}
                    </small>
                    <small>
                      {row.twilioCostUsd === null
                        ? "Aguardando conciliação Twilio"
                        : "Provedores conciliados"}
                    </small>
                  </div>
                  <div className={voice.media}>
                    <span>{mediaLabel(row.mediaState)}</span>
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={!row.audioAvailable || !!media.busy}
                        onClick={() => openMedia(row, "audio")}
                      >
                        Áudio
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={!row.transcriptAvailable || !!media.busy}
                        onClick={() => openMedia(row, "transcript")}
                      >
                        Transcrição
                      </button>
                    </div>
                  </div>
                  <dl className={voice.callDetails}>
                    <div><dt>Resultado da ligação</dt><dd><strong>{voiceOutcome(row)}</strong>{(!row.outcome || ["CONNECTED","CONFLICT","UNKNOWN"].includes(row.outcome)) && <small>{voiceAnswer(row)}</small>}</dd></div>
                    <div><dt>Motivo do encerramento</dt><dd>{voiceDisconnect(row)}{row.disconnectionReason && <code>{row.disconnectionReason}</code>}</dd></div>
                    <div><dt>Informação dos provedores</dt><dd><small>Retell: {voiceProviderStatus(row.status)}</small><small>Twilio: {voiceProviderStatus(row.twilioCallStatus)}</small></dd></div>
                  </dl>
                </article>
              ))
            )}
            <div className={styles.pagination}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span>
                Página {page} de {data.pagination.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= data.pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          </section>
          <p className={voice.footnote}>
            Uma chamada conectada pode ter sido recebida por caixa postal ou triagem automática. A identificação de pessoa depende da informação do provedor. Custos pendentes não equivalem a zero. A duração pode diferir entre
            Retell e Twilio. Uma chamada encerrada não confirma pagamento no
            Asaas. Áudios e transcrições só aparecem quando disponíveis e
            arquivados.
          </p>
        </>
      )}
    </main>
  );
}

export default withFinancialSession(FinancialVoicePage);
